import { useEffect, useRef, useState, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { engineLanding } from "@/lib/engineLanding";
import { onboardingGate, engineCheckFailure } from "@/lib/engineFunnel";
import api from "@/lib/api";
import lazyRoute from "@/lib/lazyRoute";
import EnglishRedirect from "@/components/EnglishRedirect";
import { ADVISORS_PAGE_NS, ENGINE_NS, withCatalogs } from "@/i18n/lazyCatalogs";
import { currentLang } from "@/i18n/languages";
import { initNative } from "@/lib/nativeInit";
import { initAnalytics } from "@/lib/analytics";
import { initMetaPixel } from "@/lib/metaPixel";
import { isNative } from "@/lib/appConfig";
import "@/App.css";

import AppLayout from "@/components/layout/AppLayout";
import ScrollToTop from "@/components/ScrollToTop";
import PixelPageView from "@/components/PixelPageView";
import CookieBanner from "@/components/CookieBanner";
import NativeBackButton from "@/components/native/NativeBackButton";
import SwipeBack from "@/components/native/SwipeBack";
import AppLock from "@/components/native/AppLock";
import OfflineBanner from "@/components/native/OfflineBanner";
import DeepLinks from "@/components/native/DeepLinks";
import PageLoader from "@/components/native/PageLoader";
import AccessCheckError from "@/components/engine/AccessCheckError";

// Route components are lazy-loaded so the initial bundle stays small — key for a
// fast cold start inside the native WebView.
// Las del Engine y /asesores piden a la vez sus catalogos de textos (i18n/lazyCatalogs.js). El Engine
// siempre en es (compara literales del backend en es) mas el idioma activo.
const engineRoute = (factory) => lazyRoute(withCatalogs(ENGINE_NS, () => ["es", currentLang()], factory));
const advisorsRoute = (lang) => lazyRoute(withCatalogs(ADVISORS_PAGE_NS, lang, () => import("@/pages/Asesores")));
const Landing = lazyRoute(() => import("@/pages/Landing"));
const Pricing = lazyRoute(() => import("@/pages/Pricing"));
const Asesores = advisorsRoute("es");
const AdvisorsEn = advisorsRoute("en");
const AdminEngine = engineRoute(() => import("@/pages/AdminEngine"));
const AdminEngineOrg = engineRoute(() => import("@/pages/AdminEngineOrg"));
const Login = lazyRoute(() => import("@/pages/Login"));
const Register = lazyRoute(() => import("@/pages/Register"));
const ForgotPassword = lazyRoute(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyRoute(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazyRoute(() => import("@/pages/VerifyEmail"));
const Onboarding = lazyRoute(() => import("@/pages/Onboarding"));
const SelectPlan = lazyRoute(() => import("@/pages/SelectPlan"));
const Dashboard = lazyRoute(() => import("@/pages/Dashboard"));
const Portfolios = lazyRoute(() => import("@/pages/Portfolios"));
const PortfolioDetail = lazyRoute(() => import("@/pages/PortfolioDetail"));
const Holdings = lazyRoute(() => import("@/pages/Holdings"));
const Transactions = lazyRoute(() => import("@/pages/Transactions"));
const Benchmarks = lazyRoute(() => import("@/pages/Benchmarks"));
const Risk = lazyRoute(() => import("@/pages/Risk"));
const Alerts = lazyRoute(() => import("@/pages/Alerts"));
const Reports = lazyRoute(() => import("@/pages/Reports"));
const Settings = lazyRoute(() => import("@/pages/Settings"));
const Billing = lazyRoute(() => import("@/pages/Billing"));
const Help = lazyRoute(() => import("@/pages/Help"));
const Admin = lazyRoute(() => import("@/pages/Admin"));
const Legal = lazyRoute(() => import("@/pages/Legal"));
const Dividends = lazyRoute(() => import("@/pages/Dividends"));
const Fiscal = lazyRoute(() => import("@/pages/Fiscal"));
const Referrals = lazyRoute(() => import("@/pages/Referrals"));
const ReferralLanding = lazyRoute(() => import("@/pages/ReferralLanding"));
const Connections = lazyRoute(() => import("@/pages/Connections"));
const OAuthCallback = lazyRoute(() => import("@/pages/OAuthCallback"));
const CsvImport = lazyRoute(() => import("@/pages/CsvImport"));
const Support = lazyRoute(() => import("@/pages/Support"));
const BlogList = lazyRoute(() => import("@/pages/BlogList"));
const BlogPost = lazyRoute(() => import("@/pages/BlogPost"));
const AdminBlogList = lazyRoute(() => import("@/pages/AdminBlogList"));
const AdminBlogEditor = lazyRoute(() => import("@/pages/AdminBlogEditor"));
const AdminBlogGenerate = lazyRoute(() => import("@/pages/AdminBlogGenerate"));
const AdminCampaigns = lazyRoute(() => import("@/pages/AdminCampaigns"));
const AdminCampaignEditor = lazyRoute(() => import("@/pages/AdminCampaignEditor"));
const AdminAnnouncements = lazyRoute(() => import("@/pages/AdminAnnouncements"));
const AdminCoupons = lazyRoute(() => import("@/pages/AdminCoupons"));
const AdminCouponDetail = lazyRoute(() => import("@/pages/AdminCouponDetail"));
const AdminInfluencers = lazyRoute(() => import("@/pages/AdminInfluencers"));
const AdminInfluencerDetail = lazyRoute(() => import("@/pages/AdminInfluencerDetail"));
const PortfolioPublic = lazyRoute(() => import("@/pages/PortfolioPublic"));
const Rewind = lazyRoute(() => import("@/pages/Rewind"));
const Optimize = lazyRoute(() => import("@/pages/Optimize"));
const EngineLayout = engineRoute(() => import("@/components/engine/EngineLayout"));
const EngineHome = engineRoute(() => import("@/pages/EngineHome"));
const EnginePortfolio = engineRoute(() => import("@/pages/EnginePortfolio"));
// key={id}: al pasar de una cartera a otra la página se monta de cero y no arrastra el formulario anterior.
const EnginePortfolioRoute = () => <EnginePortfolio key={useParams().id} />;
const EngineRun = engineRoute(() => import("@/pages/EngineRun"));
const EngineRuns = engineRoute(() => import("@/pages/EngineRuns"));
const EngineTeam = engineRoute(() => import("@/pages/EngineTeam"));
const EngineActivity = engineRoute(() => import("@/pages/EngineActivity"));
const EngineHelp = engineRoute(() => import("@/pages/EngineHelp"));
const EngineCompare = engineRoute(() => import("@/pages/EngineCompare"));
const EngineSign = engineRoute(() => import("@/pages/EngineSign"));
const Download = lazyRoute(() => import("@/pages/Download"));
const Radiografia = lazyRoute(() => import("@/pages/Radiografia"));
const CompensarPerdidas = lazyRoute(() => import("@/pages/CompensarPerdidas"));
const Sorteo = lazyRoute(() => import("@/pages/Sorteo"));
const Comparativa = lazyRoute(() => import("@/pages/Comparativa"));
const ContentPage = lazyRoute(() => import("@/pages/ContentPage"));
const CalculadoraFiscal = lazyRoute(() => import("@/pages/CalculadoraFiscal"));

// On native the app should open into the product, not the marketing site.
function Home() {
    const { token, loading } = useAuth();
    if (isNative()) {
        if (loading) return <PageLoader />;
        return <Navigate to={token ? "/app/dashboard" : "/login"} replace />;
    }
    return <Landing />;
}

function Private({ children }) {
    const { token, loading, user, setAuth } = useAuth();
    const location = useLocation();
    const gate = onboardingGate(user, location.pathname, isNative());
    // Un miembro del Engine con el onboarding de particulares a medias (cuentas de antes de
    // que el alta del Engine lo saltase) no se manda al onboarding dentro del Engine: se
    // pregunta a /api/engine/me y, si es miembro, se da por hecho (el backend tambien lo
    // hace al entrar). null = preguntando; "error" = no se pudo comprobar (se ofrece reintentar).
    const [engineMember, setEngineMember] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const session = useRef({ token, setAuth });
    session.current = { token, setAuth };
    useEffect(() => {
        if (gate !== "check") return undefined;
        let alive = true;
        api.get("/api/engine/me", { timeout: 4000 }).then(
            () => {
                if (!alive) return;
                setEngineMember(true);
                api.patch("/api/users/me", { onboarding_completed: true }).then((r) => {
                    if (alive && r.data) session.current.setAuth(session.current.token, r.data);
                }, () => {});
            },
            (e) => alive && setEngineMember(engineCheckFailure(e) === "error" ? "error" : false),
        );
        return () => { alive = false; };
    }, [gate, attempt]);
    if (loading) return <PageLoader />;
    if (!token) return <Navigate to="/login" replace />;
    if (gate === "check" && engineMember === null) return <PageLoader />;
    if (gate === "check" && engineMember === "error") {
        return <AccessCheckError user={user} onRetry={() => { setEngineMember(null); setAttempt((n) => n + 1); }} />;
    }
    if (gate === "onboarding" || (gate === "check" && !engineMember)) return <Navigate to="/onboarding" replace />;
    if (user && user.plan_selected === false) return <Navigate to="/select-plan" replace />;
    return children;
}

// /app a secas: la portada de un miembro del Engine es el Engine (decision D-B2).
function AppIndex() {
    const [to, setTo] = useState(null);
    useEffect(() => {
        let alive = true;
        engineLanding("/app/dashboard").then((t) => alive && setTo(t));
        return () => { alive = false; };
    }, []);
    return to ? <Navigate to={to} replace /> : <PageLoader />;
}

function SelectPlanGate() {
    const { token, loading, user } = useAuth();
    if (loading) return <PageLoader />;
    if (!token) return <Navigate to="/login" replace />;
    if (user && !user.onboarding_completed) return <Navigate to="/onboarding" replace />;
    return <SelectPlan />;
}

function AdminOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <PageLoader />;
    if (!user || user.role !== "admin") return <Navigate to="/app/dashboard" replace />;
    return children;
}

// Onboarding needs auth but must NOT require onboarding_completed (that would loop,
// since Private redirects incomplete users here). Guard on token only.
function OnboardingGate({ children }) {
    const { token, loading } = useAuth();
    if (loading) return <PageLoader />;
    if (!token) return <Navigate to="/login" replace />;
    return children;
}

function App() {
    useEffect(() => {
        initNative();
        initAnalytics();
        initMetaPixel();
    }, []);

    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <PixelPageView />
                    <NativeBackButton />
                    <SwipeBack />
                    <DeepLinks />
                    <AppLock />
                    <OfflineBanner />
                    <CookieBanner />
                    <Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/r/:code" element={<ReferralLanding />} />
                        {/* Enlace corto de afiliado: el codigo ya lo ha leido lib/acquisition.js */}
                        <Route path="/a/:code" element={<Navigate to="/" replace />} />
                        <Route path="/p/:id" element={<PortfolioPublic />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/asesores" element={<Asesores />} />
                        <Route path="/en/advisors" element={<AdvisorsEn lang="en" />} />
                        <Route path="/en" element={<EnglishRedirect />} />
                        <Route path="/advisors" element={<EnglishRedirect />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/onboarding" element={<OnboardingGate><Onboarding /></OnboardingGate>} />
                        <Route path="/select-plan" element={<SelectPlanGate />} />

                        <Route path="/descargar" element={<Download />} />
                        <Route path="/download" element={<Download />} />
                        <Route path="/radiografia" element={<Radiografia />} />
                        <Route path="/compensar-perdidas" element={<CompensarPerdidas />} />
                        <Route path="/sorteo" element={<Sorteo />} />
                        <Route path="/comparativa/:slug" element={<Comparativa />} />
                        <Route path="/prensa" element={<ContentPage path="/prensa" />} />
                        <Route path="/calculadora-fiscal" element={<CalculadoraFiscal />} />
                        <Route path="/brokers/:slug" element={<ContentPage />} />

                        <Route path="/blog" element={<BlogList />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />

                        <Route path="/legal/:slug" element={<Legal />} />
                        <Route path="/legal" element={<Navigate to="/legal/notice" replace />} />
                        {/* Enlace corto del pie de los correos de prospeccion (revision legal C-2). */}
                        <Route path="/privacidad" element={<Navigate to="/legal/privacy#contactos" replace />} />

                        <Route
                            path="/app"
                            element={
                                <Private>
                                    <AppLayout />
                                </Private>
                            }
                        >
                            <Route index element={<AppIndex />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="portfolios" element={<Portfolios />} />
                            <Route path="portfolios/:id" element={<PortfolioDetail />} />
                            <Route path="optimizar" element={<Optimize />} />
                            <Route path="optimizar/:portfolioId" element={<Optimize />} />
                            <Route path="engine" element={<EngineLayout />}>
                                <Route index element={<EngineHome />} />
                                <Route path="carteras/:id" element={<EnginePortfolioRoute />} />
                                <Route path="calculos" element={<EngineRuns />} />
                                <Route path="calculos/:runId" element={<EngineRun />} />
                                <Route path="equipo" element={<EngineTeam />} />
                                <Route path="actividad" element={<EngineActivity />} />
                                <Route path="ayuda" element={<EngineHelp />} />
                                <Route path="comparar" element={<EngineCompare />} />
                                <Route path="firma" element={<EngineSign />} />
                            </Route>
                            <Route path="holdings" element={<Holdings />} />
                            <Route path="transactions" element={<Transactions />} />
                            <Route path="benchmarks" element={<Benchmarks />} />
                            <Route path="riesgo" element={<Risk />} />
                            <Route path="alerts" element={<Alerts />} />
                            <Route path="reports" element={<Reports />} />
                            <Route path="dividends" element={<Dividends />} />
                            <Route path="rewind" element={<Rewind />} />
                            <Route path="fiscal" element={<Fiscal />} />
                            <Route path="referrals" element={<Referrals />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="billing" element={<Billing />} />
                            <Route path="help" element={<Help />} />
                            <Route path="connections" element={<Connections />} />
                            <Route path="connections/:provider/callback" element={<OAuthCallback />} />
                            <Route path="import" element={<CsvImport />} />
                            <Route path="support" element={<Support />} />
                        </Route>

                        <Route
                            path="/admin"
                            element={
                                <Private>
                                    <AdminOnly>
                                        <AppLayout />
                                    </AdminOnly>
                                </Private>
                            }
                        >
                            <Route index element={<Admin />} />
                            <Route path="blog" element={<AdminBlogList />} />
                            <Route path="blog/new" element={<AdminBlogEditor />} />
                            <Route path="blog/generate" element={<AdminBlogGenerate />} />
                            <Route path="blog/:id/edit" element={<AdminBlogEditor />} />
                            <Route path="campaigns" element={<AdminCampaigns />} />
                            <Route path="campaigns/new" element={<AdminCampaignEditor />} />
                            <Route path="campaigns/:id" element={<AdminCampaignEditor />} />
                            <Route path="announcements" element={<AdminAnnouncements />} />
                            <Route path="coupons" element={<AdminCoupons />} />
                            <Route path="coupons/:id" element={<AdminCouponDetail />} />
                            <Route path="influencers" element={<AdminInfluencers />} />
                            <Route path="influencers/:id" element={<AdminInfluencerDetail />} />
                            <Route path="engine" element={<AdminEngine />} />
                            <Route path="engine/:id" element={<AdminEngineOrg />} />
                            <Route path="engine/:orgId/calculos/:runId" element={<EngineRun admin />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </Suspense>
                </BrowserRouter>
                <Toaster position={isNative() ? "top-center" : "top-right"} />
            </AuthProvider>
        </div>
    );
}

export default App;
